'use client';

import React, { useState } from 'react';
import { Save, Calendar, Clock, Info } from 'lucide-react';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const SHIFTS = ['Day (7am–3pm)', 'Evening (3pm–11pm)', 'Night (11pm–7am)'];

interface DayAvailability {
  available: boolean;
  shifts: boolean[];
}

type WeekAvailability = Record<string, DayAvailability>;

const INITIAL_AVAIL: WeekAvailability = {
  Monday:    { available: true,  shifts: [true,  false, false] },
  Tuesday:   { available: true,  shifts: [true,  true,  false] },
  Wednesday: { available: true,  shifts: [true,  false, false] },
  Thursday:  { available: true,  shifts: [false, true,  false] },
  Friday:    { available: true,  shifts: [true,  false, false] },
  Saturday:  { available: false, shifts: [false, false, false] },
  Sunday:    { available: false, shifts: [false, false, false] },
};

const BLACKOUT_DATES = ['2024-08-12', '2024-08-13', '2024-09-02'];

export default function AvailabilitySchedule() {
  const [avail, setAvail] = useState<WeekAvailability>(INITIAL_AVAIL);
  const [maxHours, setMaxHours] = useState('40');
  const [minHours, setMinHours] = useState('32');
  const [preferredShift, setPreferredShift] = useState('Day (7am–3pm)');
  const [blackouts, setBlackouts] = useState<string[]>(BLACKOUT_DATES);
  const [newBlackout, setNewBlackout] = useState('');
  const [saved, setSaved] = useState(false);

  const toggleDay = (day: string) => {
    setAvail((prev) => ({
      ...prev,
      [day]: { ...prev[day], available: !prev[day].available, shifts: !prev[day].available ? prev[day].shifts : [false, false, false] },
    }));
  };

  const toggleShift = (day: string, idx: number) => {
    setAvail((prev) => {
      const shifts = [...prev[day].shifts];
      shifts[idx] = !shifts[idx];
      return { ...prev, [day]: { ...prev[day], shifts } };
    });
  };

  const addBlackout = () => {
    if (newBlackout && !blackouts.includes(newBlackout)) {
      setBlackouts([...blackouts, newBlackout].sort());
      setNewBlackout('');
    }
  };

  const removeBlackout = (d: string) => setBlackouts(blackouts.filter((b) => b !== d));

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  return (
    <div className="space-y-4">
      {saved && (
        <div className="flex items-center gap-2 bg-success/10 border border-success/20 text-success text-sm px-4 py-2.5 rounded-lg">
          <Info size={14} />
          Availability preferences saved successfully.
        </div>
      )}

      {/* Weekly availability grid */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Calendar size={16} className="text-primary" />
            <h2 className="text-sm font-700 text-foreground">Weekly Availability</h2>
          </div>
          <button
            onClick={handleSave}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-500 hover:bg-primary/90 transition-colors"
          >
            <Save size={12} />
            Save
          </button>
        </div>

        <div className="space-y-2">
          {/* Header */}
          <div className="grid grid-cols-[120px_1fr] gap-3 items-center mb-1">
            <span className="text-xs font-600 text-muted-foreground uppercase tracking-wide">Day</span>
            <div className="grid grid-cols-3 gap-2">
              {SHIFTS.map((s) => (
                <span key={s} className="text-xs font-600 text-muted-foreground text-center">{s}</span>
              ))}
            </div>
          </div>

          {DAYS.map((day) => (
            <div key={day} className={`grid grid-cols-[120px_1fr] gap-3 items-center p-2.5 rounded-lg transition-colors ${avail[day].available ? 'bg-muted/30' : 'bg-muted/10 opacity-60'}`}>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => toggleDay(day)}
                  className={`relative w-8 h-4.5 rounded-full transition-colors flex-shrink-0 ${avail[day].available ? 'bg-primary' : 'bg-border'}`}
                  style={{ height: '18px', width: '32px' }}
                >
                  <span
                    className={`absolute top-0.5 w-3.5 h-3.5 rounded-full bg-white shadow transition-transform ${avail[day].available ? 'translate-x-4' : 'translate-x-0.5'}`}
                  />
                </button>
                <span className="text-sm font-500 text-foreground">{day.slice(0, 3)}</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {SHIFTS.map((shift, idx) => (
                  <button
                    key={shift}
                    disabled={!avail[day].available}
                    onClick={() => toggleShift(day, idx)}
                    className={`py-1.5 rounded-md text-xs font-500 transition-colors border ${
                      avail[day].available && avail[day].shifts[idx]
                        ? 'bg-primary/10 border-primary/30 text-primary' :'bg-transparent border-border text-muted-foreground hover:border-primary/30'
                    } disabled:cursor-not-allowed`}
                  >
                    {idx === 0 ? 'Day' : idx === 1 ? 'Eve' : 'Night'}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Preferences */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <Clock size={16} className="text-primary" />
          <h2 className="text-sm font-700 text-foreground">Hour Preferences</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="text-xs font-600 text-muted-foreground uppercase tracking-wide">Min Hours / Week</label>
            <input
              type="number"
              value={minHours}
              onChange={(e) => setMinHours(e.target.value)}
              min={0}
              max={80}
              className="mt-1 w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
          <div>
            <label className="text-xs font-600 text-muted-foreground uppercase tracking-wide">Max Hours / Week</label>
            <input
              type="number"
              value={maxHours}
              onChange={(e) => setMaxHours(e.target.value)}
              min={0}
              max={80}
              className="mt-1 w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
          <div>
            <label className="text-xs font-600 text-muted-foreground uppercase tracking-wide">Preferred Shift</label>
            <select
              value={preferredShift}
              onChange={(e) => setPreferredShift(e.target.value)}
              className="mt-1 w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              {SHIFTS.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Blackout dates */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <Calendar size={16} className="text-warning" />
          <h2 className="text-sm font-700 text-foreground">Blackout Dates</h2>
          <span className="text-xs text-muted-foreground">(dates you are unavailable)</span>
        </div>
        <div className="flex gap-2 mb-4">
          <input
            type="date"
            value={newBlackout}
            onChange={(e) => setNewBlackout(e.target.value)}
            className="bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
          <button
            onClick={addBlackout}
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-500 hover:bg-primary/90 transition-colors"
          >
            Add Date
          </button>
        </div>
        {blackouts.length === 0 ? (
          <p className="text-sm text-muted-foreground">No blackout dates set.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {blackouts.map((d) => (
              <span key={d} className="flex items-center gap-1.5 bg-warning/10 text-warning text-xs font-500 px-3 py-1.5 rounded-full border border-warning/20">
                {d}
                <button onClick={() => removeBlackout(d)} className="hover:text-danger transition-colors">×</button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
